import { Product, Customer } from "../types";

const DB_NAME = "cps-pos-cache";
const DB_VERSION = 1;
const STORE = "keyval";

const KEYS = {
    products: "products",
    customers: "customers",
    lastSync: "lastSync",
} as const;

function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            if (!req.result.objectStoreNames.contains(STORE)) {
                req.result.createObjectStore(STORE);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function dbGet<T>(key: string): Promise<T | null> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readonly");
        const req = tx.objectStore(STORE).get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => reject(req.error);
    });
}

async function dbSet(key: string, value: unknown): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export const posCache = {
    async getProducts(): Promise<Product[] | null> {
        return dbGet<Product[]>(KEYS.products);
    },
    async saveProducts(products: Product[]): Promise<void> {
        return dbSet(KEYS.products, products);
    },
    async getCustomers(): Promise<Customer[] | null> {
        return dbGet<Customer[]>(KEYS.customers);
    },
    async saveCustomers(customers: Customer[]): Promise<void> {
        return dbSet(KEYS.customers, customers);
    },
    async getLastSync(): Promise<number | null> {
        return dbGet<number>(KEYS.lastSync);
    },
    async saveLastSync(ts: number): Promise<void> {
        return dbSet(KEYS.lastSync, ts);
    },
    async saveAll(products: Product[], customers: Customer[], ts: number): Promise<void> {
        await Promise.all([
            dbSet(KEYS.products, products),
            dbSet(KEYS.customers, customers),
            dbSet(KEYS.lastSync, ts),
        ]);
    },
};
